"use client";

import { useEffect, useMemo, useRef } from "react";
import { useParams } from "next/navigation";
import { useSnapshot } from "valtio";
import log from "logbeacon";
import { I18nUtil } from "@utils/i18n";
import { trackEvent } from "@/src/utils/tracking";
import type {
  OnboardingWizardStep,
  QuizAnswers,
} from "@/src/components/OnboardingWizard/types";
import { useOspApiClientProvider } from "@/src/hooks/useOspApiClientProvider";
import { useRequestHeader } from "@/src/hooks/useRequestHeader";
import { rootStore } from "@/src/store";
import { mapGoalIdToGoalType } from "./mapGoalIdToGoalType";
import { mapQuizAnswersToV3Request } from "./mapQuizAnswersToV3Request";
import {
  OnboardingV3ApiError,
  runOnboardingPlanPreviewRequest,
  runOnboardingV3GenerateRequest,
} from "./onboardingV3Api";
import {
  getOnboardingV3Session,
  patchOnboardingV3Session,
  persistOnboardingV3Session,
} from "./onboardingV3SessionStorage";
import { useMemoizedFn } from "ahooks";

export interface UseOnboardingWizardApiFlowParams {
  step: OnboardingWizardStep;
  answers: QuizAnswers;
  simulateLoadingError?: boolean;
  onGenerateSuccess: () => void;
  onGenerateError: () => void;
}

export const useOnboardingWizardApiFlow = ({
  step,
  answers,
  simulateLoadingError = false,
  onGenerateSuccess,
  onGenerateError,
}: UseOnboardingWizardApiFlowParams) => {
  const { chatApi } = useOspApiClientProvider();
  const { headers } = useRequestHeader();
  const { fingerprint } = useSnapshot(rootStore.user);
  const params = useParams<{ locale?: string }>();
  const generateStartedRef = useRef(false);
  const previewStartedRef = useRef(false);

  const requestHeaders = useMemo(
    () => ({
      ...headers,
      ...(fingerprint ? { guestId: String(fingerprint) } : {}),
    }),
    [headers, fingerprint],
  );

  const language = useMemo(
    () =>
      I18nUtil.getBackgroundLanguageKey(
        params?.locale ?? I18nUtil.getCurrentLocale(),
      ),
    [params?.locale],
  );

  const goalType = useMemo(
    () => mapGoalIdToGoalType(answers.goalId),
    [answers.goalId],
  );

  const runGenerate = useMemoizedFn(async () => {
    if (simulateLoadingError) {
      trackEvent("ob_loading_error");
      onGenerateError();
      return;
    }

    try {
      const body = {
        ...mapQuizAnswersToV3Request(answers, goalType),
        context: { language },
      };
      const data = await runOnboardingV3GenerateRequest(
        chatApi,
        requestHeaders,
        body,
      );

      persistOnboardingV3Session({
        selectedGoalType: goalType,
        goalId: answers.goalId,
        previewId: data.previewId,
        resultValues: data.resultValues,
        generateCompletedAt: Date.now(),
        ...(data.fitnessScore ? { fitnessScore: data.fitnessScore } : {}),
      });

      onGenerateSuccess();
    } catch (error) {
      log.warn("[onboarding] v3 generate failed", error);
      trackEvent("ob_loading_error", {
        code: error instanceof OnboardingV3ApiError ? error.code : "UNKNOWN",
      });
      onGenerateError();
    }
  });

  useEffect(() => {
    if (step !== "loading") {
      generateStartedRef.current = false;
      return;
    }
    if (generateStartedRef.current) {
      return;
    }
    generateStartedRef.current = true;
    void runGenerate();
  }, [runGenerate, step]);

  useEffect(() => {
    if (step !== "result") {
      previewStartedRef.current = false;
      return;
    }

    const session = getOnboardingV3Session();
    if (!session?.previewId || session.planPreview?.draftId) {
      if (session?.selectedGoalType) {
        trackEvent("ob_result_view", { goal: session.selectedGoalType });
      }
      return;
    }

    if (previewStartedRef.current) {
      return;
    }
    previewStartedRef.current = true;

    void (async () => {
      try {
        const planPreview = await runOnboardingPlanPreviewRequest(
          chatApi,
          requestHeaders,
          session.previewId,
        );
        patchOnboardingV3Session({ planPreview });
      } catch (error) {
        log.warn("[onboarding] plan-preview prefetch failed", error);
        trackEvent("ob_loading_error", {
          phase: "plan_preview",
          code: error instanceof OnboardingV3ApiError ? error.code : "UNKNOWN",
        });
      } finally {
        trackEvent("ob_result_view", {
          goal: session.selectedGoalType,
        });
      }
    })();
  }, [chatApi, requestHeaders, step]);

  return {
    goalType,
    session: getOnboardingV3Session(),
  };
};
